import React from 'react'

export interface TableData {
  headers: string[]
  rows: string[][]
}

/** Convert HTML <br> tags to newlines so they are not shown as literal text. */
export function normalizeHtmlBreaks(text: string): string {
  return text.replace(/<br\s*\/?>/gi, '\n')
}

interface TableRendererProps {
  table: TableData
  className?: string
}

/**
 * Renders markdown tables in a visually structured format
 * Detects and highlights specific columns for better readability
 */
export function TableRenderer({ table, className = '' }: TableRendererProps) {
  if (!table.headers || table.headers.length === 0 || !table.rows) {
    return null
  }

  return (
    <StandardTableView
      table={table}
      className={className}
      stickyFirstColumn={table.headers.length > 2}
    />
  )
}

interface StandardTableViewProps extends TableRendererProps {
  stickyFirstColumn?: boolean
}

/**
 * Standard table view for regular and comparison tables
 */
function StandardTableView({ table, className = '', stickyFirstColumn = false }: StandardTableViewProps) {
  const labelHeaderClass = stickyFirstColumn
    ? 'sticky left-0 z-20 bg-muted/70 font-semibold border-r border-border/50'
    : ''
  const labelCellClass = stickyFirstColumn
    ? 'sticky left-0 z-10 bg-muted/50 font-medium border-r border-border/50'
    : ''

  return (
    <div className={`overflow-x-auto max-w-full my-4 rounded-lg border border-border/40 ${className}`}>
      <table className="min-w-max w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-primary/10 to-primary/5 border-b-2 border-primary/20">
            {table.headers.map((header, i) => (
              <th
                key={i}
                className={`px-4 py-3 text-left font-semibold text-foreground border-r border-primary/10 last:border-r-0 align-top min-w-[120px] ${
                  stickyFirstColumn && i === 0 ? labelHeaderClass : 'whitespace-nowrap'
                }`}
              >
                {formatMarkdownText(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={`group border-b border-border/50 hover:bg-muted/50 transition-colors ${
                rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/20'
              }`}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`px-4 py-3 text-sm text-foreground border-r border-border/30 last:border-r-0 align-top min-w-[140px] max-w-[360px] ${
                    stickyFirstColumn && cellIndex === 0
                      ? `${labelCellClass} group-hover:bg-muted/60`
                      : ''
                  }`}
                >
                  {formatCellContent(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Format markdown in cell text (bold, italic, code, links)
 */
function formatMarkdownText(text: string): React.ReactNode {
  const normalized = normalizeHtmlBreaks(text)
  let result: React.ReactNode[] = []
  let remaining = normalized

  // Pattern for all markdown: bold, italic, code, links
  const markdownPattern = /(\*\*[^*]+\*\*|__[^_]+__|_[^_]+_|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g

  let lastIndex = 0
  const matches = [...normalized.matchAll(markdownPattern)]

  matches.forEach((match, i) => {
    const matchText = match[0]
    const startIndex = match.index!

    // Add text before this match
    if (startIndex > lastIndex) {
      result.push(normalized.substring(lastIndex, startIndex))
    }

    // Process the match
    if (matchText.startsWith('**') && matchText.endsWith('**')) {
      result.push(<strong key={i}>{matchText.slice(2, -2)}</strong>)
    } else if (matchText.startsWith('__') && matchText.endsWith('__')) {
      result.push(<strong key={i}>{matchText.slice(2, -2)}</strong>)
    } else if (matchText.startsWith('_') && matchText.endsWith('_')) {
      result.push(<em key={i}>{matchText.slice(1, -1)}</em>)
    } else if (matchText.startsWith('*') && matchText.endsWith('*')) {
      result.push(<em key={i}>{matchText.slice(1, -1)}</em>)
    } else if (matchText.startsWith('`') && matchText.endsWith('`')) {
      result.push(
        <code key={i} className="bg-muted/50 px-1.5 py-0.5 rounded text-xs font-mono border">
          {matchText.slice(1, -1)}
        </code>
      )
    } else if (matchText.startsWith('[')) {
      const linkMatch = matchText.match(/\[([^\]]+)\]\(([^)]+)\)/)
      if (linkMatch) {
        result.push(
          <a
            key={i}
            href={linkMatch[2]}
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkMatch[1]}
          </a>
        )
      }
    }

    lastIndex = startIndex + matchText.length
  })

  // Add remaining text
  if (lastIndex < normalized.length) {
    result.push(normalized.substring(lastIndex))
  }

  return result.length > 0 ? result : normalized
}

/**
 * Format cell content for display
 * Handles lists, keywords highlighting, and text formatting
 */
function formatCellContent(cell: string): React.ReactNode {
  if (!cell || cell.trim() === '') return '-'

  const normalizedCell = normalizeHtmlBreaks(cell).trim()

  // Multi-line cell content (e.g. bullets separated by <br> in AI tables)
  if (normalizedCell.includes('\n')) {
    const lines = normalizedCell.split('\n').map((line) => line.trim()).filter(Boolean)
    if (lines.length > 1) {
      return (
        <div className="space-y-1">
          {lines.map((line, index) => (
            <div key={index} className="text-xs leading-relaxed">
              {formatMarkdownText(line)}
            </div>
          ))}
        </div>
      )
    }
  }

  const cellValue = normalizedCell

  // Check if cell contains a Python/JSON array (e.g., ['item1', 'item2', ...])
  // Use a simple check - if it starts with [ and ends with ], try to parse it
  const trimmedCell = cellValue.trim()
  if (trimmedCell.startsWith('[') && trimmedCell.endsWith(']')) {
    try {
      // Clean up the string for JSON parsing
      // 1. Unescape single quotes
      // 2. Replace single quotes with double quotes (but not apostrophes inside words)
      let jsonString = trimmedCell
        .replace(/\\'/g, "'") // Unescape escaped single quotes
        .replace(/'([^']*?)'/g, '"$1"') // Replace single quotes around strings with double quotes
      
      const items = JSON.parse(jsonString)
      
      if (Array.isArray(items) && items.length > 0) {
        // For very long lists, show a scrollable container with max-height
        const isLongList = items.length > 10
        
        return (
          <div className={`space-y-1 ${isLongList ? 'max-h-48 overflow-y-auto pr-2' : ''}`}>
            {items.map((item: any, i: number) => {
              // Handle both string items and object items
              let displayText: string
              
              if (typeof item === 'object' && item !== null) {
                // For objects like {'name': 'Arabe', 'level': 'C2'}
                if ('name' in item && 'level' in item) {
                  return (
                    <div key={i} className="flex items-center justify-between text-xs bg-muted/30 px-2 py-1 rounded">
                      <span className="font-medium">{formatMarkdownText(String(item.name))}</span>
                      <span className="text-primary/70 ml-2 bg-primary/10 px-2 py-1 rounded">
                        {String(item.level)}
                      </span>
                    </div>
                  )
                }
                displayText = JSON.stringify(item)
              } else {
                displayText = String(item)
              }
              
              return (
                <div key={i} className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">•</span>
                  <span className="break-words text-xs leading-relaxed">{formatMarkdownText(displayText)}</span>
                </div>
              )
            })}
          </div>
        )
      }
    } catch (e) {
      // If parsing fails, continue with other formatting options
      console.debug('Failed to parse Python array:', e)
    }
  }

  // Check for dictionary-like content with curly braces (e.g., {'name': 'John', 'level': 'C1'})
  const jsonObjectPattern = /^\{.+\}$/
  if (jsonObjectPattern.test(cellValue.trim())) {
    try {
      const jsonString = cellValue.trim().replace(/'/g, '"')
      const obj = JSON.parse(jsonString)
      
      if (typeof obj === 'object' && !Array.isArray(obj)) {
        return (
          <div className="space-y-1">
            {Object.entries(obj).map(([key, value], i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="font-medium">{formatMarkdownText(key)}</span>
                <span className="text-primary/70 ml-2 bg-primary/10 px-2 py-1 rounded">
                  {String(value)}
                </span>
              </div>
            ))}
          </div>
        )
      }
    } catch (e) {
      // If parsing fails, continue with other formatting options
    }
  }

  // Check if cell contains a comma-separated list
  if (cellValue.includes(',') && !cellValue.includes('http')) {
    const items = cellValue.split(',').map(s => s.trim()).filter(s => s.length > 0)
    if (items.length > 1 && items.every(item => item.length < 50)) {
      return (
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={i} className="flex items-start">
              <span className="text-primary mr-2">•</span>
              <span className="break-words">{formatMarkdownText(item)}</span>
            </div>
          ))}
        </div>
      )
    }
  }

  // Check for parentheses patterns (languages with levels)
  const parenthesesPattern = /([^()]+)\s*\(([^)]+)\)/g
  const matches = [...cellValue.matchAll(parenthesesPattern)]
  
  if (matches.length > 0 && matches.length <= 5) {
    return (
      <div className="space-y-1">
        {matches.map((match, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="font-medium">{formatMarkdownText(match[1].trim())}</span>
            <span className="text-primary/70 ml-2 bg-primary/10 px-2 py-1 rounded">
              {match[2].trim()}
            </span>
          </div>
        ))}
      </div>
    )
  }

  // Check for date patterns
  if (cellValue.match(/\d{1,2}\s+(ans?|mois|jours?|years?|months?|days?)/i)) {
    return (
      <div className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full font-semibold text-xs">
        {formatMarkdownText(cellValue)}
      </div>
    )
  }

  // Regular text with markdown formatting
  return (
    <p className="whitespace-normal break-words">
      {formatMarkdownText(cellValue)}
    </p>
  )
}
